import { Box, Typography, Fade } from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { demoTypography, retroColors } from './theme';

/**
 * Reusable step slide container with title, content area, and key stat footer.
 */
export default function StepSlide({
    title,
    subtitle,
    children,
    keyStat,
    behindTheScenes,
    visible = true,
}) {
    return (
        <Fade in={visible} timeout={600}>
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                px: { xs: 3, md: 6, lg: 10 },
                py: { xs: 2, md: 3 },
                overflow: 'auto',
                maxHeight: 'calc(100vh - 120px)',
            }}>
                {/* Title area */}
                <Box sx={{ mb: 3 }}>
                    <Typography sx={demoTypography.stepTitle}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ ...demoTypography.stepSubtitle, mt: 1 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {/* Main content */}
                <Box sx={{ flex: 1 }}>
                    {children}
                </Box>

                {/* Behind the scenes callout */}
                {behindTheScenes && (
                    <Box sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'rgba(95, 212, 196, 0.06)',
                        border: '1px solid rgba(95, 212, 196, 0.15)',
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.65rem',
                            color: 'rgba(95, 212, 196, 0.5)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            mb: 0.5,
                        }}>
                            Behind the Scenes
                        </Typography>
                        <Typography sx={demoTypography.technical}>
                            {behindTheScenes}
                        </Typography>
                    </Box>
                )}

                {/* Key stat footer */}
                {keyStat && (
                    <Box sx={{
                        mt: 2,
                        py: 1.5,
                        borderTop: `1px solid rgba(178, 140, 80, 0.15)`,
                    }}>
                        <Typography sx={{
                            ...demoTypography.quote,
                            fontSize: { xs: '0.95rem', md: '1.1rem' },
                            color: vortxColors.gold,
                            fontStyle: 'normal',
                            fontWeight: 500,
                        }}>
                            {keyStat}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Fade>
    );
}

/**
 * Stat card used within step slides.
 */
export function StatCard({ icon, value, label, color = vortxColors.gold }) {
    return (
        <Box sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: retroColors.surface,
            border: `1px solid rgba(178, 140, 80, 0.1)`,
            textAlign: 'center',
            minWidth: 140,
        }}>
            {icon && (
                <Box sx={{ color, mb: 1, fontSize: '1.5rem' }}>{icon}</Box>
            )}
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontWeight: 700,
                fontSize: '1.4rem',
                color,
            }}>
                {value}
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.body,
                fontSize: '0.75rem',
                color: retroColors.muted,
                mt: 0.5,
            }}>
                {label}
            </Typography>
        </Box>
    );
}

/**
 * Two-column layout within a step.
 */
export function SplitView({ left, right, ratio = '1fr 1fr' }) {
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: ratio },
            gap: { xs: 3, md: 4 },
            flex: 1,
        }}>
            <Box>{left}</Box>
            <Box>{right}</Box>
        </Box>
    );
}

/**
 * Bullet list with gold markers.
 */
export function DemoBullets({ items, color = vortxColors.gold }) {
    return (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {items.map((item, i) => (
                <Box
                    component="li"
                    key={i}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        mb: 1.5,
                    }}
                >
                    <Box sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: color,
                        mt: '0.5em',
                        flexShrink: 0,
                    }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.body,
                        fontSize: '0.95rem',
                        color: vortxColors.white,
                        lineHeight: 1.6,
                    }}>
                        {item}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}
